import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  StatusBar,
  ActivityIndicator,
  RefreshControl,
  TouchableOpacity,
  Modal,
  ScrollView,
  Animated,
  Image,
  Dimensions,
} from 'react-native';
import Icon from '../components/Icon';
import { useTheme } from '../context/ThemeContext';
import { userSummaryAPI, userTradesAPI } from '../services/api';
import CustomDatePicker from '../components/CustomDatePicker';
import { formatTradeDateTime } from '../utils/dateFormatter';

const { width: WINDOW_WIDTH } = Dimensions.get('window');

const HistoryScreen = ({ navigation }) => {
  // All hooks must be called at the top level - never inside conditionals
  const { colors, isDarkMode } = useTheme();
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('Last month');
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [isSelectingStartDate, setIsSelectingStartDate] = useState(true);
  const [activeTab, setActiveTab] = useState('POSITIONS');
  const [totalCommission, setTotalCommission] = useState(0);
  const [commissionData, setCommissionData] = useState(null);
  const [walletBalance, setWalletBalance] = useState(0);
  const [userId, setUserId] = useState(null);
  const [totalProfit, setTotalProfit] = useState(0);
  const [totalDeposit, setTotalDeposit] = useState(0);
  const [totalWithdrawal, setTotalWithdrawal] = useState(0);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [selectedServer, setSelectedServer] = useState('fizmoFXMarket-Server');
  const [userSummary, setUserSummary] = useState(null);
  const slideAnim = useRef(new Animated.Value(-WINDOW_WIDTH * 0.75)).current;

  // Fetch user profile for drawer
  const fetchUserProfile = async () => {
    try {
      // Temporary dummy data
      setUserProfile({
        name: 'Demo User',
        userId: '1000077',
        image: null
      });
    } catch (error) {
      console.error('Error fetching user profile:', error);
    } finally {
      setIsLoadingProfile(false);
    }
  };

  const fetchSelectedServer = async () => {
    try {
      // You can implement server selection logic here if needed
      setSelectedServer('fizmoFXMarket-Server');
    } catch (error) {
      console.error('Error fetching selected server:', error);
    }
  };

  const toggleDrawer = () => {
    if (drawerVisible) {
      Animated.timing(slideAnim, {
        toValue: -WINDOW_WIDTH * 0.75,
        duration: 300,
        useNativeDriver: true,
      }).start(() => {
        setDrawerVisible(false);
      });
    } else {
      setDrawerVisible(true);
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 300,
        useNativeDriver: true,
      }).start();
    }
  };

  const fetchHistory = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch User Summary API
      console.log('🔗 Fetching User Summary API...');
      const summaryResponse = await userSummaryAPI.getUserSummary().catch(err => {
        console.log('User Summary API error:', err);
        return null;
      });
      
      if (summaryResponse?.success) {
        console.log('📊 User Summary Response:', JSON.stringify(summaryResponse, null, 2));
        
        const data = summaryResponse.data;
        setUserSummary(data);
        
        // Set values from new API response structure
        setWalletBalance(data.actualBalance || 0);
        setTotalProfit(data.totalProfit || 0); // Use totalProfit instead of netProfit
        setTotalDeposit(data.totalDeposited || 0);
        setTotalWithdrawal(data.totalWithdrawn || 0);
        setTotalCommission(data.totalCommissionDeducted || 0);
        
        console.log('📊 Summary Data Set:');
        console.log('Balance:', data.actualBalance);
        console.log('Total Profit:', data.totalProfit);
        console.log('Total Deposited:', data.totalDeposited);
        console.log('Total Withdrawn:', data.totalWithdrawn);
        console.log('Commission Deducted:', data.totalCommissionDeducted);
        
      } else {
        console.log('❌ User Summary API failed, using dummy data');
        // Fallback to dummy data if API fails
        setUserSummary(null);
        setWalletBalance(0);
        setTotalProfit(0);
        setTotalDeposit(0);
        setTotalWithdrawal(0);
        setTotalCommission(0);
      }
      
      // Fetch User Trades API - Only if user has deposited money
      console.log('🔗 Fetching User Trades API...');
      
      // Check if user has made any deposits
      const hasDeposited = (summaryResponse?.data?.totalDeposited || 0) > 0;
      const hasBalance = (summaryResponse?.data?.actualBalance || 0) > 0;
      
      console.log('💰 User Deposit Check:');
      console.log('- Total Deposited:', summaryResponse?.data?.totalDeposited || 0);
      console.log('- Current Balance:', summaryResponse?.data?.actualBalance || 0);
      console.log('- Has Deposited:', hasDeposited);
      console.log('- Has Balance:', hasBalance);
      
      if (!hasDeposited) {
        console.log('❌ User has not deposited any amount - No trades to show');
        setTransactions([]);
        setFilteredTransactions([]);
        return;
      }
      
      const tradesResponse = await userTradesAPI.getUserTrades({ limit: 1000 }).catch(err => {
        console.log('User Trades API error:', err);
        return null;
      });
      
      if (tradesResponse?.success && Array.isArray(tradesResponse.data)) {
        console.log('📊 User Trades Response:', JSON.stringify(tradesResponse, null, 2));
        
        const transactionsArray = tradesResponse.data;
        const formattedData = [];
        
        // Process each transaction
        transactionsArray.forEach((transaction, index) => {
          try {
            // Process all transactions, including N/A ones
            const buyPrice = parseFloat(transaction.buyPrice) || 0;
            const sellPrice = parseFloat(transaction.sellPrice) || 0;
            const quantity = parseFloat(transaction.quantity) || 0;
            const userReceived = parseFloat(transaction.userReceived) || 0;
            const commissionAmount = parseFloat(transaction.commission) || 0;
            
            // Calculate actual profit/loss from price difference
            let calculatedProfit = 0;
            if (buyPrice > 0 && sellPrice > 0 && quantity > 0) {
              // For sell trades: (sellPrice - buyPrice) * quantity
              if (transaction.tradeType === 'SELL') {
                calculatedProfit = (buyPrice- sellPrice  ) * quantity;
              }
              // For buy trades: (sellPrice - buyPrice) * quantity (same as sell)
              else if (transaction.tradeType === 'BUY') {
                calculatedProfit = (sellPrice - buyPrice) * quantity;
              }
            } else {
              // For commission entries or trades without price data, use userReceived
              calculatedProfit = userReceived;
            }
            
            // Determine display name
            let displaySymbol = 'Commission';
            if (transaction.tradeName && transaction.tradeName !== 'N/A') {
              displaySymbol = transaction.tradeName;
            }
            
            // Determine trade type
            let tradeType = 'buy';
            if (transaction.tradeType === 'SELL') {
              tradeType = 'sell';
            } else if (transaction.tradeType === 'BUY') {
              tradeType = 'buy';
            }
            
            formattedData.push({
              id: transaction.id || `trade_${index}_${Date.now()}`,
              symbol: displaySymbol,
              type: tradeType,
              volume: transaction.quantity?.toString() || '0',
              openPrice: (buyPrice / 100).toFixed(2),
              closePrice: (sellPrice / 100).toFixed(2),
              timestamp: formatTradeDateTime(transaction.date),
              profit: calculatedProfit.toFixed(2),
              totalProfit: calculatedProfit.toFixed(2), // Use calculated profit for color display
              commissionAmount: commissionAmount.toFixed(2),
              commission: commissionAmount, // For Balance Card
              showCommissionCard: true, // Always show commission card
              rawTransaction: transaction // Keep original for debugging
            });
          } catch (err) {
            console.error('Error formatting transaction:', err, transaction);
          }
        });
        
        console.log('📊 Formatted Transactions:', formattedData.length);
        setTransactions(formattedData);
        setFilteredTransactions(formattedData);
      } else {
        console.log('❌ User Trades API failed or no data');
        setTransactions([]);
        setFilteredTransactions([]);
      }
      
    } catch (error) {
      console.error('Error in fetchHistory:', error);
      // Set error state values
      setTransactions([]);
      setFilteredTransactions([]);
      setWalletBalance(0);
      setTotalProfit(0);
      setTotalDeposit(0);
      setTotalWithdrawal(0);
      setTotalCommission(0);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  const filterByPeriod = (period) => {
    setSelectedPeriod(period);
    setShowDatePicker(false);

    if (period === 'Custom period') {
      // Open custom date picker for start date
      setIsSelectingStartDate(true);
      setShowCustomDatePicker(true);
      return;
    }

    const today = new Date();
    let startDate = new Date();

    switch(period) {
      case 'Today':
        startDate = today;
        break;
      case 'Last week':
        startDate.setDate(today.getDate() - 7);
        break;
      case 'Last month':
        startDate.setMonth(today.getMonth() - 1);
        break;
      case 'Last 3 months':
        startDate.setMonth(today.getMonth() - 3);
        break;
      default:
        setFilteredTransactions(transactions);
        return;
    }

    const filtered = transactions.filter(item => {
      const itemDateStr = item.timestamp.split(' ')[0];
      const [day, month, year] = itemDateStr.split('.');
      const itemDate = new Date(year, month - 1, day);
      return itemDate >= startDate && itemDate <= today;
    });

    setFilteredTransactions(filtered);
  };

  const handleCustomDateSelect = (date) => {
    if (isSelectingStartDate) {
      setCustomStartDate(date);
      setIsSelectingStartDate(false);
      setShowCustomDatePicker(true); // Keep picker open for end date
    } else {
      setCustomEndDate(date);
      setShowCustomDatePicker(false);
      
      // Filter transactions with custom date range
      if (customStartDate && date) {
        filterByCustomDateRange(customStartDate, date);
      }
    }
  };

  const filterByCustomDateRange = (startDate, endDate) => {
    const filtered = transactions.filter(item => {
      const itemDateStr = item.timestamp.split(' ')[0];
      const [day, month, year] = itemDateStr.split('.');
      const itemDate = new Date(year, month - 1, day);
      
      // Ensure startDate is before endDate
      const start = startDate <= endDate ? startDate : endDate;
      const end = startDate <= endDate ? endDate : startDate;
      
      return itemDate >= start && itemDate <= end;
    });

    setFilteredTransactions(filtered);
  };

  const handleCustomDatePickerClose = () => {
    setShowCustomDatePicker(false);
    // Reset states if user cancels
    if (isSelectingStartDate) {
      setCustomStartDate(null);
      setCustomEndDate(null);
    } else {
      // If user cancels end date selection, go back to start date
      setIsSelectingStartDate(true);
    }
  };

  const getDateRangeText = (period) => {
    if (period === 'Custom period' && customStartDate && customEndDate) {
      const formatDate = (date) => {
        return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' }).replace(/\//g, '/');
      };
      return `${formatDate(customStartDate)} - ${formatDate(customEndDate)}`;
    }

    const today = new Date();
    let startDate = new Date();

    switch(period) {
      case 'Today':
        return today.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' }).replace(/\//g, '/');
      case 'Last week':
        startDate.setDate(today.getDate() - 7);
        break;
      case 'Last month':
        startDate.setMonth(today.getMonth() - 1);
        break;
      case 'Last 3 months':
        startDate.setMonth(today.getMonth() - 3);
        break;
      case 'Custom period':
        return '12/1/24 - 11/12/25'; // Default text when no custom dates selected
      default:
        return '';
    }

    const formatDate = (date) => {
      return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' }).replace(/\//g, '/');
    };

    return `${formatDate(startDate)} - ${formatDate(today)}`;
  };

  const onRefresh = () => {
    setRefreshing(true);
    fetchHistory();
  };

  useEffect(() => {
    fetchUserProfile();
    fetchSelectedServer();
    fetchHistory();
  }, [fetchHistory]);

  const menuItems = [
    { id: '1', icon: 'trending-up-outline', label: 'Trade', screen: 'Trades' },
    { id: '2', icon: 'newspaper-outline', label: 'News', screen: null },
    { id: '3', icon: 'mail-outline', label: 'Mailbox', screen: null },
    { id: '4', icon: 'calendar-outline', label: 'Journal', screen: null },
    { id: '5', icon: 'settings-outline', label: 'Settings', screen: 'Profile' },
    { id: '6', icon: 'calendar-outline', label: 'Economic calendar', badge: 'Ads', screen: null },
    { id: '7', icon: 'people-outline', label: 'Traders Community', screen: null },
    { id: '8', icon: 'code-slash-outline', label: 'MQL5 Algo Trading', screen: null },
    { id: '9', icon: 'help-circle-outline', label: 'User guide', screen: null },
    { id: '10', icon: 'information-circle-outline', label: 'About', screen: null },
  ];

  const renderHistoryItem = ({ item }) => {
    return (
      <View>
        {/* Trade Card */}
        <View style={styles.tradeCard}>
          {/* Trade Header */}
          <View style={styles.tradeHeader}>
            <View style={styles.tradeHeaderLeft}>
              <Text style={[styles.symbolText, { color: colors.text }]}>{item.symbol}, </Text>
              <Text style={[styles.typeText, item.type === 'buy' ? styles.buyType : styles.sellType]}>
                {item.type} 
              </Text>
              <Text style={[styles.volumeText, item.type === 'sell' && styles.sellType]}> {parseFloat(item.volume || 0) % 1 === 0 ? Math.round(parseFloat(item.volume || 0)) : parseFloat(item.volume || 0).toFixed(2)}</Text>
            </View>
            <View style={styles.tradeHeaderRight}>
              <Text style={styles.timestampText}>{item.timestamp}</Text>
              <Text style={[
                styles.profitText, 
                { color: parseFloat(item.totalProfit) > 0 ? '#4A90E2' : parseFloat(item.totalProfit) < 0 ? '#EF4444' : colors.text }
              ]}>
                {parseFloat(item.totalProfit) > 0 ? '+' : parseFloat(item.totalProfit) < 0 ? '-' : ''}{Math.abs(parseFloat(item.totalProfit || 0)).toFixed(2)}
              </Text>
            </View>
          </View>

          {/* Price Info */}
          <View style={styles.priceRow}>
            <Text style={styles.priceText}>
              {parseFloat(item.openPrice || 0).toFixed(2)}→{parseFloat(item.closePrice || 0).toFixed(2)}
            </Text>
          </View>
        </View>
        <View style={[styles.cardSeparatorLine, { backgroundColor: colors.border }]} />
        
        {/* Balance Card showing Admin Commission - Only show for commission entries */}
        {item.showCommissionCard && (
          <>
            <View style={styles.balanceCard}>
              <View style={styles.balanceRow}>
                <Text style={[styles.balanceLabel, { color: colors.text }]}>Balance</Text>
                <Text style={styles.balanceTimestamp}>{item.timestamp}</Text>
              </View>
              <View style={styles.balanceDetailRow}>
                <Text style={styles.balanceDetail}>Copy Commission Withdraw</Text>
                <Text style={styles.balanceProfit}>
                  -{parseFloat(item.commissionAmount || item.commission || 0).toFixed(2)}
                </Text>
              </View>
            </View>
            <View style={[styles.cardSeparatorLine, { backgroundColor: colors.border }]} />
          </>
        )}
      </View>
    );
  };

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      <StatusBar barStyle={isDarkMode ? "light-content" : "dark-content"} backgroundColor={colors.background} />

      {/* Header Section */}
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <TouchableOpacity style={styles.menuButton} onPress={toggleDrawer}>
            <Icon name="menu" size={24} color={colors.primary} />
          </TouchableOpacity>
          <View>
            <Text style={[styles.headerTitle, { color: colors.primary }]}>History</Text>
          </View>
        </View>
        <View style={styles.headerRight}>
          <TouchableOpacity 
            style={[
              styles.calendarButton, 
              { borderColor: colors.primary },
              selectedDate && { backgroundColor: colors.primary }
            ]}
            onPress={() => setShowDatePicker(true)}
          >
            <Icon name="calendar" size={20} color={selectedDate ? '#FFFFFF' : colors.primary} />
          </TouchableOpacity>
          <TouchableOpacity style={styles.menuButton}>
            <Icon name="arrow-down" size={24} color={colors.primary} />
          </TouchableOpacity>
        </View>
      </View>

      {/* Tabs */}
      <View style={[styles.tabsWrap, { borderBottomColor: colors.border }]}>
        {['POSITIONS', 'ORDERS', 'DEALS'].map((tab) => (
          <TouchableOpacity
            key={tab}
            style={[styles.tab, activeTab === tab && { borderBottomColor: colors.text }]}
            onPress={() => setActiveTab(tab)}
          >
            <Text
              style={[
                styles.tabText,
                { color: colors.textTertiary },
                activeTab === tab && { color: colors.text },
              ]}
            >
              {tab}
            </Text>
          </TouchableOpacity>
        ))}
      </View>

      {/* Summary Section - Always show summary information */}
      <View style={[styles.summarySection, { backgroundColor: colors.background }]}>
        <View style={styles.summaryRow}>
          <Text style={[styles.summaryLabel, { color: colors.text }]}>Profit:</Text>
          <View style={styles.dotsContainer}>
            <Text style={[styles.summaryDots, { color: colors.border }]} numberOfLines={1}>••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••</Text>
          </View>
          <Text style={[styles.summaryValue, { 
            color: (totalProfit || 0) >= 0 ? colors.primary : colors.error 
          }]}>
            {(totalProfit || 0) >= 0 ? '+' : ''}{parseFloat(totalProfit || 0).toFixed(2)}
          </Text>
        </View>
        <View style={styles.summaryRow}>
          <Text style={[styles.summaryLabel, { color: colors.text }]}>Deposited:</Text>
          <View style={styles.dotsContainer}>
            <Text style={[styles.summaryDots, { color: colors.border }]} numberOfLines={1}>••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••</Text>
          </View>
          <Text style={[styles.summaryValue, { color: colors.text }]}>
            {parseFloat(totalDeposit || 0).toFixed(2)}
          </Text>
        </View>
        <View style={styles.summaryRow}>
          <Text style={[styles.summaryLabel, { color: colors.text }]}>Withdrawal:</Text>
          <View style={styles.dotsContainer}>
            <Text style={[styles.summaryDots, { color: colors.border }]} numberOfLines={1}>••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••</Text>
          </View>
          <Text style={[styles.summaryValue, { color: colors.error }]}>
            {parseFloat(totalWithdrawal || 0).toFixed(2)}
          </Text>
        </View>
        <View style={styles.summaryRow}>
          <Text style={[styles.summaryLabel, { color: colors.text }]}>Commission:</Text>
          <View style={styles.dotsContainer}>
            <Text style={[styles.summaryDots, { color: colors.border }]} numberOfLines={1}>••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••</Text>
          </View>
          <Text style={[styles.summaryValue, { 
            color: (totalCommission || 0) > 0 ? colors.error : colors.textSecondary 
          }]}>
            {parseFloat(totalCommission || 0).toFixed(2)}
            {commissionData?.count && (totalCommission || 0) > 0 && (
              <Text style={{ fontSize: 11, color: colors.textSecondary }}> ({commissionData.count} txns)</Text>
            )}
          </Text>
        </View>
        <View style={styles.summaryRow}>
          <Text style={[styles.summaryLabel, { color: colors.text }]}>Balance:</Text>
          <View style={styles.dotsContainer}>
            <Text style={[styles.summaryDots, { color: colors.border }]} numberOfLines={1}>••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••</Text>
          </View>
          <Text style={[styles.summaryValue, { 
            color: (walletBalance || 0) > 0 ? colors.text : colors.textSecondary 
          }]}>
            {parseFloat(walletBalance || 0).toFixed(2)}
          </Text>
        </View>
      </View>

      {/* Period Picker Modal */}
      <Modal
        visible={showDatePicker}
        transparent
        animationType="fade"
        onRequestClose={() => setShowDatePicker(false)}
      >
        <TouchableOpacity 
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setShowDatePicker(false)}
        >
          <View 
            style={[styles.periodPickerContainer, { backgroundColor: colors.cardBackground }]}
            onStartShouldSetResponder={() => true}
          >
            <TouchableOpacity
              style={[
                styles.periodItem,
                { borderBottomColor: colors.border },
                selectedPeriod === 'Today' && styles.periodItemSelected
              ]}
              onPress={() => filterByPeriod('Today')}
            >
              <Icon name="calendar-outline" size={20} color={colors.text} style={styles.periodIcon} />
              <View style={styles.periodContent}>
                <Text style={[styles.periodTitle, { color: colors.text }]}>Today</Text>
                <Text style={[styles.periodSubtitle, { color: colors.textSecondary }]}>
                  {getDateRangeText('Today')}
                </Text>
              </View>
              {selectedPeriod === 'Today' && (
                <Icon name="checkmark" size={20} color={colors.primary} />
              )}
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.periodItem,
                { borderBottomColor: colors.border },
                selectedPeriod === 'Last week' && styles.periodItemSelected
              ]}
              onPress={() => filterByPeriod('Last week')}
            >
              <Icon name="calendar-outline" size={20} color={colors.text} style={styles.periodIcon} />
              <View style={styles.periodContent}>
                <Text style={[styles.periodTitle, { color: colors.text }]}>Last week</Text>
                <Text style={[styles.periodSubtitle, { color: colors.textSecondary }]}>
                  {getDateRangeText('Last week')}
                </Text>
              </View>
              {selectedPeriod === 'Last week' && (
                <Icon name="checkmark" size={20} color={colors.primary} />
              )}
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.periodItem,
                { borderBottomColor: colors.border },
                selectedPeriod === 'Last month' && styles.periodItemSelected
              ]}
              onPress={() => filterByPeriod('Last month')}
            >
              <Icon name="calendar-outline" size={20} color={colors.text} style={styles.periodIcon} />
              <View style={styles.periodContent}>
                <Text style={[styles.periodTitle, { color: colors.text }]}>Last month</Text>
                <Text style={[styles.periodSubtitle, { color: colors.textSecondary }]}>
                  {getDateRangeText('Last month')}
                </Text>
              </View>
              {selectedPeriod === 'Last month' && (
                <Icon name="checkmark" size={20} color={colors.primary} />
              )}
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.periodItem,
                { borderBottomColor: colors.border },
                selectedPeriod === 'Last 3 months' && styles.periodItemSelected
              ]}
              onPress={() => filterByPeriod('Last 3 months')}
            >
              <Icon name="time-outline" size={20} color={colors.text} style={styles.periodIcon} />
              <View style={styles.periodContent}>
                <Text style={[styles.periodTitle, { color: colors.text }]}>Last 3 months</Text>
                <Text style={[styles.periodSubtitle, { color: colors.textSecondary }]}>
                  {getDateRangeText('Last 3 months')}
                </Text>
              </View>
              {selectedPeriod === 'Last 3 months' && (
                <Icon name="checkmark" size={20} color={colors.primary} />
              )}
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.periodItem,
                selectedPeriod === 'Custom period' && styles.periodItemSelected
              ]}
              onPress={() => filterByPeriod('Custom period')}
            >
              <Icon name="calendar" size={20} color={colors.text} style={styles.periodIcon} />
              <View style={styles.periodContent}>
                <Text style={[styles.periodTitle, { color: colors.text }]}>Custom period</Text>
                <Text style={[styles.periodSubtitle, { color: colors.textSecondary }]}>
                  {getDateRangeText('Custom period')}
                </Text>
              </View>
              {selectedPeriod === 'Custom period' && (
                <Icon name="checkmark" size={20} color={colors.primary} />
              )}
            </TouchableOpacity>
          </View>
        </TouchableOpacity>
      </Modal>

      {/* Separator Line */}
      <View style={[styles.separatorLine, { backgroundColor: colors.border }]} />

      {/* List */}
      {loading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      ) : (
        <FlatList
          data={(totalDeposit || 0) > 0 ? filteredTransactions : []}
          renderItem={renderHistoryItem}
          keyExtractor={(item) => item.id}
          contentContainerStyle={{ paddingBottom: 1 }}
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={onRefresh}
              colors={[colors.primary]}
              tintColor={colors.primary}
            />
          }
          ListEmptyComponent={
            <View style={styles.emptyContainer}>
              <Text style={styles.emptyText}>
                {(totalDeposit || 0) > 0 
                  ? "No trades found. Trade history will appear here when available."
                  : "Please deposit money to your wallet to start trading and view transaction history."
                }
              </Text>
            </View>
          }
        />
      )}

      {/* DRAWER MENU */}
      <Modal
        visible={drawerVisible}
        transparent
        animationType="none"
        onRequestClose={toggleDrawer}
      >
        <TouchableOpacity 
          style={styles.drawerOverlay} 
          activeOpacity={1} 
          onPress={toggleDrawer}
        >
          <Animated.View 
            style={[
              styles.drawerContainer,
              { transform: [{ translateX: slideAnim }], backgroundColor: colors.background, borderRightColor: colors.border }
            ]}
            onStartShouldSetResponder={() => true}
          >
            {/* Drawer Header */}
            <View style={[styles.drawerHeader, { borderBottomColor: colors.border }]}>
              <View style={styles.drawerProfile}>
                <View style={[styles.drawerAvatar, { backgroundColor: `${colors.primary}33`, borderColor: colors.primary }]}>
                  {userProfile?.image ? (
                    <Image 
                      source={{ uri: `https://a77.onetradebot.com/${userProfile.image}` }} 
                      style={styles.drawerAvatarImage}
                    />
                  ) : (
                    <Icon name="person" size={24} color={colors.primary} />
                  )}
                </View>
                <View style={styles.drawerInfo}>
                  {isLoadingProfile ? (
                    <ActivityIndicator size="small" color={colors.primary} />
                  ) : (
                    <>
                      <Text style={[styles.drawerName, { color: colors.text }]}>
                        {userProfile?.name || 'User'}
                      </Text>
                      <Text style={[styles.drawerServer, { color: colors.textTertiary }]}>
                        {userProfile?.userId || '1000077'} - {selectedServer}
                      </Text>
                      <TouchableOpacity 
                        onPress={() => {
                          toggleDrawer();
                          navigation.navigate('EditProfile');
                        }}
                        style={styles.manageAccountsContainer}
                      >
                        <Text style={[styles.manageAccountsText, { color: colors.primary }]}>
                          Manage accounts
                        </Text>
                      </TouchableOpacity>
                    </>
                  )}
                </View>
              </View>
            </View>

            {/* Menu Items */}
            <View style={styles.menuSection}>
              {menuItems.map((item) => (
                <TouchableOpacity
                  key={item.id}
                  style={styles.menuItem}
                  onPress={() => {
                    toggleDrawer();
                    if (item.screen) {
                      navigation.navigate(item.screen);
                    }
                  }}
                >
                  <Icon name={item.icon} size={20} color={colors.text} />
                  <View style={styles.labelContainer}>
                    <Text style={[styles.menuLabel, { color: colors.text }]}>{item.label}</Text>
                    {item.badge === 'Ads' && (
                      <View style={styles.adsContainer}>
                        <Text style={styles.adsText}>{item.badge}</Text>
                      </View>
                    )}
                  </View>
                  {item.badge && item.badge !== 'Ads' && (
                    <View style={[styles.badge, { backgroundColor: colors.error }]}>
                      <Text style={styles.badgeText}>{item.badge}</Text>
                    </View>
                  )}
                </TouchableOpacity>
              ))}
            </View>
          </Animated.View>
        </TouchableOpacity>
      </Modal>

      {/* Custom Date Picker */}
      <CustomDatePicker
        visible={showCustomDatePicker}
        onClose={handleCustomDatePickerClose}
        onDateSelect={handleCustomDateSelect}
        initialDate={isSelectingStartDate ? customStartDate : customEndDate}
        isSelectingStartDate={isSelectingStartDate}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },

  header: {
    paddingTop: 12,
    paddingBottom: 15,
    paddingHorizontal: 16,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },

  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },

  menuButton: {
    padding: 4,
  },

  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },

  calendarButton: {
    padding: 8,
    borderRadius: 6,
    backgroundColor: 'transparent',
    borderWidth: 1,
  },

  calendarButtonActive: {
  },

  headerTitle: {
    fontSize: 16,
    fontWeight: '600',
  },

  headerSubtitle: {
    fontSize: 21,
    marginTop: 2,
  },

  tabsWrap: {
    flexDirection: 'row',
    borderBottomWidth: 1,
  },

  tab: {
    flex: 1,
    paddingVertical: 12,
    alignItems: 'center',
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
  },

  tabText: {
    fontSize: 13,
    fontWeight: '500',
    letterSpacing: 0.5,
  },

  separatorLine: {
    height: 1,
    backgroundColor: '#d6dbe2ff',
    marginHorizontal: -5,
  },

  summarySection: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#0B0F2F',
  },

  summaryRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 2,
  },

  summaryLabel: {
    color: '#FFFFFF',
    fontSize: 15,
    fontWeight: '600',
  },

  dotsContainer: {
    flex: 1,
    marginHorizontal: 4,
    overflow: 'hidden',
  },

  summaryDots: {
    color: '#d6dbe2ff',
    fontSize: 8,
    letterSpacing: 1,
  },

  summaryValue: {
    color: '#FFFFFF',
    fontSize: 15,
    fontWeight: '600',
    textAlign: 'right',
  },

  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },

  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: 100,
  },

  emptyText: {
    fontSize: 16,
    color: '#6B7280',
  },

  tradeCard: {
    paddingHorizontal: 16,
    paddingVertical: 1,
    
  },

  tradeHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },

  tradeHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
  },

  symbolText: {
    color: '#9CA3AF',
    fontSize: 15,
    fontWeight: '400',
  },

  typeText: {
    fontSize: 15,
    fontWeight: '600',
  },

  buyType: {
    color: '#4A90E2',
  },

  sellType: {
    color: '#EF4444',
  },

  volumeText: {
    color: '#4A90E2',
    fontSize: 15,
    fontWeight: '400',
  },

  tradeHeaderRight: {
    alignItems: 'flex-end',
  },

  timestampText: {
    color: '#9CA3AF',
    fontSize: 10,
    marginBottom: 2,
    fontWeight: '400',
  },

  profitText: {
    color: '#4A90E2',
    fontSize: 15,
    fontWeight: '400',
  },

  priceRow: {
    marginTop: -20,
    marginBottom: 4,
  },

  priceText: {
    color: '#9CA3AF',
    fontSize: 13,
    fontWeight: '400',
    textAlignVertical: 'center',
    lineHeight: 18,
  },

  cardSeparatorLine: {
    height: 1,
    backgroundColor: '#d6dbe2ff',
    marginHorizontal: -5,
  },

  balanceCard: {
    paddingHorizontal: 16,
    paddingVertical: 2,
  },

  balanceRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: -3,
  },

  balanceLabel: {
    fontSize: 15,
    fontWeight: '400',
  },

  balanceTimestamp: {
    color: '#d6dbe2ff',
    fontSize: 10,
    fontWeight: '400',
  },

  balanceDetailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },

  balanceDetail: {
    color: '#808080',
    fontSize: 11,
    fontWeight: '400',
  },

  balanceProfit: {
    color: '#EF4444',
    fontSize: 13,
    fontWeight: '400',
  },

  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
  },

  periodPickerContainer: {
    backgroundColor: '#2C2C2E',
    borderRadius: 8,
    overflow: 'hidden',
    width: 200,
    position: 'absolute',
    top: 75,
    right: 4,
  },

  periodItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderBottomWidth: 0.5,
    borderBottomColor: '#3A3A3C',
  },

  periodItemSelected: {
    backgroundColor: 'rgba(74, 144, 226, 0.1)',
  },

  periodIcon: {
    marginRight: 8,
    color: '#FFFFFF',
  },

  periodContent: {
    flex: 1,
  },

  periodTitle: {
    fontSize: 13,
    fontWeight: '500',
    color: '#FFFFFF',
    marginBottom: 1,
  },

  periodSubtitle: {
    fontSize: 11,
    color: '#8E8E93',
  },

  // Drawer Styles
  drawerOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },

  drawerContainer: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: WINDOW_WIDTH * 0.75,
    backgroundColor: '#000000',
    borderRightWidth: 1,
    borderRightColor: '#1F2937',
  },

  drawerHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: 16,
    paddingTop: 50,
    borderBottomWidth: 1,
    borderBottomColor: '#1F2937',
  },

  drawerProfile: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    flex: 1,
  },

  drawerAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(74, 144, 226, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: '#4A90E2',
    overflow: 'hidden',
  },

  drawerAvatarImage: {
    width: '100%',
    height: '100%',
  },

  drawerInfo: {
    flex: 1,
  },

  drawerName: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 4,
  },

  drawerServer: {
    color: '#9CA3AF',
    fontSize: 12,
    marginBottom: 2,
  },

  manageAccountsContainer: {
    marginTop: 2,
    paddingTop: 2,
  },

  manageAccountsText: {
    fontSize: 14,
    fontWeight: '500',
  },

  menuSection: {
    paddingTop: 8,
    flex: 1,
  },

  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 5,
    paddingHorizontal: 16,
    gap: 16,
  },

  labelContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },

  menuLabel: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '400',
  },

  badge: {
    backgroundColor: '#EF4444',
    borderRadius: 10,
    paddingHorizontal: 8,
    paddingVertical: 2,
    minWidth: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },

  badgeText: {
    color: '#FFFFFF',
    fontSize: 10,
    fontWeight: '600',
  },

  adsContainer: {
    borderWidth: 1,
    borderColor: '#4A90E2',
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 2,
    backgroundColor: 'transparent',
  },

  adsText: {
    color: '#4A90E2',
    fontSize: 12,
    fontWeight: '500',
  },
});

export default HistoryScreen;
